import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ChevronRight, Lock, Plus } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Skeleton,
  Textarea,
  notify,
} from '@noissue/os-ui';
import { adminApi, type AdminGroupSummary, type ModuleAccess } from './admin-api';
import { groupPath } from './paths';
import { ApiError } from '../api';

/**
 * Access control: the list of groups.
 *
 * Replaces the old users × modules matrix, which grew a column per module and could
 * only say "has it or doesn't". Access is granted to GROUPS ONLY — see admin-access.ts
 * — so this is the one place access is edited, and the People view is read-only.
 */
export function AdminPage() {
  const qc = useQueryClient();
  const groups = useQuery({ queryKey: ['admin', 'groups'], queryFn: adminApi.groups });
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Access control</h1>
          <p className="text-sm text-muted-foreground">
            People get access through the groups they belong to. Changes take effect on their
            next request — no sign-out needed.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="size-4" /> New group
        </Button>
      </div>

      {groups.isPending && <Skeleton className="h-64 w-full" />}
      {groups.isError && (
        <Card className="p-6 text-sm text-destructive">
          Couldn&rsquo;t load groups. {(groups.error as Error).message}
        </Card>
      )}

      <div className="space-y-3">
        {(groups.data ?? []).map((g) => (
          <GroupRow key={g.id} group={g} />
        ))}
      </div>

      <NewGroupDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={() => qc.invalidateQueries({ queryKey: ['admin', 'groups'] })}
      />
    </div>
  );
}

/** One-line summary of what a group can do, so the list is scannable. */
export function AccessSummary({ access }: { access: ModuleAccess[] }) {
  if (access.length === 0) {
    return <span className="text-xs text-muted-foreground">No access granted</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {access.map((a) => (
        <Badge key={a.moduleId} variant="secondary" className="font-normal">
          {a.moduleName}: {a.custom ? 'Custom' : (a.level ?? 'None')}
          {a.extras.length > 0 && ` +${a.extras.length}`}
        </Badge>
      ))}
    </div>
  );
}

function GroupRow({ group }: { group: AdminGroupSummary }) {
  return (
    <Link
      to={groupPath(group.id)}
      className="block rounded-md border bg-card p-4 transition-colors hover:bg-accent/40"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="font-medium">{group.name}</span>
            {group.system && (
              // Built-in groups can't be deleted; say so rather than offering a button
              // that would 409.
              <Badge variant="outline" className="gap-1">
                <Lock className="size-3" /> Built-in
              </Badge>
            )}
            {group.source === 'workspace' && <Badge variant="outline">Workspace</Badge>}
            <span className="text-xs text-muted-foreground">
              {group.memberCount} {group.memberCount === 1 ? 'member' : 'members'}
            </span>
          </div>
          {group.description && (
            <p className="text-sm text-muted-foreground">{group.description}</p>
          )}
          <AccessSummary access={group.access} />
        </div>
        <ChevronRight className="mt-1 size-4 shrink-0 text-muted-foreground" />
      </div>
    </Link>
  );
}

function NewGroupDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const create = useMutation({
    mutationFn: () => adminApi.createGroup({ name, description }),
    onSuccess: () => {
      notify.success(`Created “${name}”.`);
      setName('');
      setDescription('');
      onOpenChange(false);
      onCreated();
    },
    // The server rejects duplicate names (case-insensitively) with a message naming the
    // existing group — surface it rather than a generic failure.
    onError: (e) => notify.error(e instanceof ApiError ? e.message : 'Could not create the group.'),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New group</DialogTitle>
          <DialogDescription>
            Name it after the job it describes — &ldquo;Sales Manager&rdquo; rather than
            &ldquo;Quoting Edit&rdquo;. You&rsquo;ll choose what it can do next.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="group-name">Name</Label>
            <Input
              id="group-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Sales Manager"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="group-description">Description</Label>
            <Textarea
              id="group-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional — who belongs in this group and why."
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate()} disabled={!name.trim() || create.isPending}>
            {create.isPending ? 'Creating…' : 'Create group'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
